import { useMutation } from "@apollo/client";
import useNotifier from "@dashboard/hooks/useNotifier";
import { importImageUploadMutation } from "@dashboard/imports/mutations";
import { Text } from "@saleor/macaw-ui-next";
import { type DragEvent, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { messages } from "./messages";

const COLOR_ACCENT = "#2563eb";
const COLOR_BORDER = "#d1d5db";
const ACCEPTED = ".jpg,.jpeg,.png,.webp,.gif,.avif";

interface UploadResult {
  filename: string;
  saved: boolean;
  status: string;
  matchedCount: number;
  message: string;
}

const statusColor = (status: string): string => {
  if (status === "OK") return "#16a34a";

  if (status === "WARNING") return "#d97706";

  return "#dc2626";
};

/**
 * Drag-and-drop (or click-to-browse) uploader that sends image files straight
 * into the backend import folder (MEDIA_ROOT/import/) via the
 * `importImageUpload` mutation. Each file is classified server-side so the
 * user immediately sees which product/variant(s) it maps to — then they can
 * hit "Scan Folder & Generate Sheet" to build the import sheet.
 */
export const ImageUploadDropzone = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ completed: number; total: number } | null>(
    null,
  );

  const [upload, { loading }] = useMutation(importImageUploadMutation);

  const sendFiles = async (files: File[]) => {
    if (files.length === 0) return;

    setResults([]);
    setUploadProgress({ completed: 0, total: files.length });

    const uploadedResults: UploadResult[] = [];
    const errors: string[] = [];

    // Keep each multipart request small so a large selection does not exceed
    // the reverse proxy's request-size limit. Sequential uploads also preserve
    // the user's file order and allow every image to be attempted after an error.
    for (const [index, file] of files.entries()) {
      try {
        const { data } = await upload({ variables: { files: [file] } });
        const payload = data?.importImageUpload;

        if (payload?.importErrors?.length) {
          errors.push(
            ...payload.importErrors.map(
              (error: { message?: string | null }) => error.message ?? "Upload failed",
            ),
          );
          uploadedResults.push({
            filename: file.name,
            saved: false,
            status: "ERROR",
            matchedCount: 0,
            message: payload.importErrors
              .map((error: { message?: string | null }) => error.message)
              .filter(Boolean)
              .join(", "),
          });
          setResults([...uploadedResults]);
          continue;
        }

        uploadedResults.push(...(payload?.results ?? []));
        setResults([...uploadedResults]);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Upload failed";

        errors.push(`${file.name}: ${message}`);
        uploadedResults.push({
          filename: file.name,
          saved: false,
          status: "ERROR",
          matchedCount: 0,
          message,
        });
        setResults([...uploadedResults]);
      } finally {
        setUploadProgress({ completed: index + 1, total: files.length });
      }
    }

    const saved = uploadedResults.filter(result => result.saved).length;
    const matched = uploadedResults.filter(result => result.saved && result.status === "OK").length;
    const unmatched = uploadedResults.filter(
      result => result.saved && result.status === "WARNING",
    ).length;
    const failed = files.length - saved;
    const summary = intl.formatMessage(messages.uploadSummary, {
      saved,
      matched,
      unmatched,
      failed,
    });

    if (failed > 0 || unmatched > 0) {
      notify({
        status: failed > 0 ? "error" : "warning",
        title: intl.formatMessage(failed > 0 ? messages.uploadFailed : messages.uploadReviewTitle),
        text: [summary, ...errors].join(" "),
      });
    } else {
      notify({
        status: "success",
        title: intl.formatMessage(messages.uploadDone, { count: saved }),
        text: summary,
      });
    }

    setUploadProgress(null);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);

    if (!loading) {
      sendFiles(Array.from(e.dataTransfer.files ?? []));
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={e => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        style={{
          border: `1.5px dashed ${dragging ? COLOR_ACCENT : COLOR_BORDER}`,
          borderRadius: 8,
          padding: "16px 12px",
          textAlign: "center",
          cursor: loading ? "wait" : "pointer",
          backgroundColor: dragging ? "rgba(37,99,235,0.06)" : "transparent",
          opacity: loading ? 0.7 : 1,
          transition: "all 0.15s ease",
        }}
      >
        <Text size={2} color="default2" display="block">
          {loading ? (
            <FormattedMessage {...messages.uploading} />
          ) : (
            <FormattedMessage {...messages.dropzoneHint} />
          )}
        </Text>
        {uploadProgress && (
          <Text size={1} color="default2" display="block">
            <FormattedMessage {...messages.uploadProgress} values={uploadProgress} />
          </Text>
        )}
        <Text size={1} color="default2" display="block">
          <FormattedMessage {...messages.stagedNote} />
        </Text>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          multiple
          disabled={loading}
          style={{ display: "none" }}
          onChange={e => {
            if (!loading) {
              sendFiles(Array.from(e.target.files ?? []));
            }

            e.target.value = "";
          }}
        />
      </div>

      {results.length > 0 && (
        <div
          style={{
            maxHeight: 140,
            overflowY: "auto",
            border: `1px solid ${COLOR_BORDER}`,
            borderRadius: 6,
            padding: 8,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {results.map((r, i) => (
            <div key={i} style={{ fontSize: 11, lineHeight: 1.4 }}>
              <span style={{ color: statusColor(r.status), fontWeight: 600 }}>
                {r.saved ? "✓" : "✗"}
              </span>{" "}
              <span style={{ fontWeight: 600 }}>{r.filename}</span>
              <span>
                {" — "}
                <FormattedMessage
                  {...(r.saved
                    ? r.status === "OK"
                      ? messages.uploadedMatched
                      : messages.uploadedUnmatched
                    : messages.notUploaded)}
                />
                {r.matchedCount > 0 ? ` (${r.matchedCount} variants)` : ""}
              </span>
              {r.message && <span style={{ color: "#6b7280" }}> — {r.message}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
